import { useState, useEffect, useCallback } from 'react';
import { useThemeInfo, AVAILABLE_THEMES } from '../context/ThemeContext';
import { useTheme } from 'next-themes';

const ThemeVisualizer = () => {
  const [colorValues, setColorValues] = useState<Record<string, string>>({});
  const { theme, } = useTheme();
  const { saveThemePreference, isThemeSyncing } = useThemeInfo();
  
  // Function to get color values and update state
  const updateAllColorValues = useCallback(() => {
    const newColorValues: Record<string, string> = {};
    const variables = [
      'background', 'foreground', 'card', 'card-foreground', 'popover', 'popover-foreground',
      'primary', 'primary-foreground', 'secondary', 'secondary-foreground',
      'muted', 'muted-foreground', 'accent', 'accent-foreground', 'destructive',
      'border', 'input', 'ring', 'chart-1', 'chart-2', 'chart-3', 'chart-4', 'chart-5',
      'sidebar', 'sidebar-foreground', 'sidebar-primary', 'sidebar-primary-foreground',
      'sidebar-accent', 'sidebar-accent-foreground', 'sidebar-border', 'sidebar-ring'
    ];
    
    variables.forEach(variable => {
      try {
        const value = getComputedStyle(document.documentElement).getPropertyValue(`--${variable}`).trim();
        newColorValues[variable] = value;
      } catch (error) {
        newColorValues[variable] = 'Error reading color';
      }
    });
    
    setColorValues(newColorValues);
  }, []);
  
  // Get computed colors from CSS variables - use the cached values
  const getColorValue = (variable: string) => {
    return colorValues[variable] || 'Loading...';
  };
  
  // Function to create a contrasting text color based on background lightness
  const getContrastText = (bgColor: string | undefined) => {
    // Simple algorithm to determine if text should be light or dark
    // For OKLCH colors, we're mostly concerned with the first value (lightness)
    const match = bgColor && bgColor.match(/oklch\(([0-9.]+)/);
    if (match && match[1]) {
      const lightness = parseFloat(match[1]);
      return lightness > 0.6 ? '#000000' : '#ffffff';
    }
    return '#ffffff'; // Default to white text
  };
  
  const changeTheme = async (themeValue: string) => {
    await saveThemePreference(themeValue);
    
    // Update color values after theme change
    setTimeout(() => {
      updateAllColorValues();
    }, 50);
  };
  
  useEffect(() => {
    // Initial color value update
    updateAllColorValues();
    
    // Set up an interval to periodically refresh color values
    const intervalId = setInterval(() => {
      updateAllColorValues();
    }, 500); // Update every 500ms
    
    return () => {
      clearInterval(intervalId);
    };
  }, [updateAllColorValues]);
  
  const colorGroups = [
    {
      name: 'Main',
      colors: [
        { name: 'Background', variable: 'background' },
        { name: 'Foreground', variable: 'foreground' },
        { name: 'Card', variable: 'card' },
        { name: 'Card Foreground', variable: 'card-foreground' },
      ]
    },
    {
      name: 'UI Elements',
      colors: [
        { name: 'Primary', variable: 'primary' },
        { name: 'Primary Foreground', variable: 'primary-foreground' },
        { name: 'Secondary', variable: 'secondary' },
        { name: 'Secondary Foreground', variable: 'secondary-foreground' },
        { name: 'Muted', variable: 'muted' },
        { name: 'Muted Foreground', variable: 'muted-foreground' },
        { name: 'Accent', variable: 'accent' },
        { name: 'Accent Foreground', variable: 'accent-foreground' },
        { name: 'Destructive', variable: 'destructive' },
        { name: 'Border', variable: 'border' },
        { name: 'Input', variable: 'input' },
        { name: 'Ring', variable: 'ring' },
      ]
    },
    {
      name: 'Chart Colors',
      colors: [
        { name: 'Chart 1', variable: 'chart-1' },
        { name: 'Chart 2', variable: 'chart-2' },
        { name: 'Chart 3', variable: 'chart-3' },
        { name: 'Chart 4', variable: 'chart-4' },
        { name: 'Chart 5', variable: 'chart-5' },
      ]
    },
    {
      name: 'Sidebar',
      colors: [
        { name: 'Sidebar', variable: 'sidebar' },
        { name: 'Sidebar Foreground', variable: 'sidebar-foreground' },
        { name: 'Sidebar Primary', variable: 'sidebar-primary' },
        { name: 'Sidebar Primary Foreground', variable: 'sidebar-primary-foreground' },
        { name: 'Sidebar Accent', variable: 'sidebar-accent' },
        { name: 'Sidebar Accent Foreground', variable: 'sidebar-accent-foreground' },
        { name: 'Sidebar Border', variable: 'sidebar-border' },
        { name: 'Sidebar Ring', variable: 'sidebar-ring' },
      ]
    }
  ];
  
  return (
    <div className="h-screen flex flex-col">
      {/* Sticky header - just the title and theme buttons */}
      <div className="sticky top-0 z-10 pt-6 pb-4 px-6">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-3xl font-bold mb-6">Soda Track Theme Visualizer</h1>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-2 mb-2">
            {AVAILABLE_THEMES.map(themeInfo => (
              <button
                key={themeInfo.value}
                onClick={() => changeTheme(themeInfo.value)}
                className={`px-4 py-3 rounded-lg border transition-colors ${
                  theme === themeInfo.value 
                    ? 'bg-primary text-primary-foreground ring-2 ring-primary' 
                    : 'hover:bg-muted'
                }`}
                disabled={isThemeSyncing}
              >
                {themeInfo.name}
                {isThemeSyncing && theme === themeInfo.value}
              </button>
            ))}
          </div>
        </div>
      </div>
      
      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto pb-6 my-6">
        <div className="max-w-5xl mx-auto px-6">
          {/* Current theme display */}
          <div className="mb-6">
            <div className="mb-4 p-4 rounded-lg bg-card border">
              <div className="text-lg font-medium mb-2">Current Theme: <span className="font-bold">{AVAILABLE_THEMES.find(t => t.value === theme)?.name || 'Default'}</span></div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                <div className="p-4 rounded bg-background border text-foreground">Background</div>
                <div className="p-4 rounded bg-primary text-primary-foreground">Primary</div>
                <div className="p-4 rounded bg-secondary text-secondary-foreground">Secondary</div>
                <div className="p-4 rounded bg-accent text-accent-foreground">Accent</div>
                <div className="p-4 rounded bg-muted text-muted-foreground">Muted</div>
              </div>
            </div>
          </div>
          
          {/* Rest of the original content - unchanged */}
          <div className="mb-8">
            <h2 className="text-xl font-bold mb-4">UI Component Examples</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-6 rounded-lg bg-card border">
                <h3 className="text-lg font-semibold mb-3">Buttons</h3>
                <div className="flex flex-wrap gap-3">
                  <button className="px-4 py-2 rounded-md bg-primary text-primary-foreground">Primary</button>
                  <button className="px-4 py-2 rounded-md bg-secondary text-secondary-foreground">Secondary</button>
                  <button className="px-4 py-2 rounded-md bg-accent text-accent-foreground">Accent</button>
                  <button className="px-4 py-2 rounded-md bg-destructive text-primary-foreground">Destructive</button>
                  <button className="px-4 py-2 rounded-md border">Outline</button>
                  <button className="px-4 py-2 rounded-md bg-muted text-muted-foreground">Muted</button>
                </div>
              </div>
              
              <div className="p-6 rounded-lg bg-card border">
                <h3 className="text-lg font-semibold mb-3">Form Elements</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Input Field</label>
                    <input 
                      type="text" 
                      className="w-full px-3 py-2 rounded-md border bg-background" 
                      placeholder="Type something..."
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <input type="checkbox" className="h-4 w-4 rounded accent-primary" />
                    <label>Checkbox</label>
                  </div>
                  <div className="flex items-center gap-3">
                    <input type="radio" className="h-4 w-4 rounded-full accent-primary" name="radio" />
                    <label>Radio Option</label>
                  </div>
                </div>
              </div>
              
              <div className="p-6 rounded-lg bg-card border">
                <h3 className="text-lg font-semibold mb-3">Card Examples</h3>
                <div className="space-y-3">
                  <div className="p-3 rounded-md bg-background border">
                    <h4 className="font-medium">Default Card</h4>
                    <p className="text-sm text-muted-foreground">This is a simple card component</p>
                  </div>
                  <div className="p-3 rounded-md bg-primary/10 border border-primary/30">
                    <h4 className="font-medium text-primary">Primary Card</h4>
                    <p className="text-sm text-primary/80">With primary styling</p>
                  </div>
                </div>
              </div>
              
              <div className="p-6 rounded-lg bg-card border">
                <h3 className="text-lg font-semibold mb-3">Text Styles</h3>
                <div className="space-y-2">
                  <p className="text-2xl font-bold">Heading Text</p>
                  <p className="text-lg font-medium">Subheading Text</p>
                  <p>Regular body text</p>
                  <p className="text-sm text-muted-foreground">Muted small text</p>
                  <p className="text-primary">Primary colored text</p>
                  <p className="text-destructive">Destructive text</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {colorGroups.map(group => (
              <div key={group.name} className="p-4 rounded-lg bg-card border">
                <h2 className="text-xl font-bold mb-4">{group.name} Colors</h2>
                <div className="space-y-3">
                  {group.colors.map(color => (
                    <div key={color.variable} className="flex items-center gap-3">
                      <div 
                        className="w-12 h-12 rounded-md border flex-shrink-0" 
                        style={{ background: `var(--${color.variable})` }}
                      ></div>
                      <div className="flex-grow">
                        <div className="font-medium">{color.name}</div>
                        <div className="text-sm text-muted-foreground font-mono">
                          {getColorValue(color.variable)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-8">
            <h2 className="text-xl font-bold mb-4">All Theme Colors</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {[
                { name: 'Background', variable: 'background' },
                { name: 'Foreground', variable: 'foreground' },
                { name: 'Card', variable: 'card' },
                { name: 'Card Foreground', variable: 'card-foreground' },
                { name: 'Popover', variable: 'popover' },
                { name: 'Popover Foreground', variable: 'popover-foreground' },
                { name: 'Primary', variable: 'primary' },
                { name: 'Primary Foreground', variable: 'primary-foreground' },
                { name: 'Secondary', variable: 'secondary' },
                { name: 'Secondary Foreground', variable: 'secondary-foreground' },
                { name: 'Muted', variable: 'muted' },
                { name: 'Muted Foreground', variable: 'muted-foreground' },
                { name: 'Accent', variable: 'accent' },
                { name: 'Accent Foreground', variable: 'accent-foreground' },
                { name: 'Destructive', variable: 'destructive' },
                { name: 'Border', variable: 'border' },
                { name: 'Input', variable: 'input' },
                { name: 'Ring', variable: 'ring' },
                { name: 'Chart 1', variable: 'chart-1' },
                { name: 'Chart 2', variable: 'chart-2' },
                { name: 'Chart 3', variable: 'chart-3' },
                { name: 'Chart 4', variable: 'chart-4' },
                { name: 'Chart 5', variable: 'chart-5' },
                { name: 'Sidebar', variable: 'sidebar' },
                { name: 'Sidebar Foreground', variable: 'sidebar-foreground' },
                { name: 'Sidebar Primary', variable: 'sidebar-primary' },
                { name: 'Sidebar Primary Foreground', variable: 'sidebar-primary-foreground' },
                { name: 'Sidebar Accent', variable: 'sidebar-accent' },
                { name: 'Sidebar Accent Foreground', variable: 'sidebar-accent-foreground' },
                { name: 'Sidebar Border', variable: 'sidebar-border' },
                { name: 'Sidebar Ring', variable: 'sidebar-ring' }
              ].map(color => {
                const colorValue = getColorValue(color.variable);
                const textColor = getContrastText(colorValue);
                
                return (
                  <div 
                    key={`${color.variable}-${theme}`}
                    className="p-4 rounded-lg border flex flex-col h-32"
                    style={{ 
                      backgroundColor: `var(--${color.variable})`,
                      color: textColor
                    }}
                  >
                    <div className="font-medium mb-1">{color.name}</div>
                    <div className="text-xs opacity-80 font-mono mt-auto">
                      {colorValue}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
          <div className="mt-8 p-6 rounded-lg bg-card border">
            <h2 className="text-xl font-bold mb-4">Chart Colors Preview</h2>
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[250px] h-64 relative rounded-lg overflow-hidden">
                <div className="absolute inset-0 flex flex-col">
                  <div className="flex-1 bg-chart-1"></div>
                  <div className="flex-1 bg-chart-2"></div>
                  <div className="flex-1 bg-chart-3"></div>
                  <div className="flex-1 bg-chart-4"></div>
                  <div className="flex-1 bg-chart-5"></div>
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="font-bold text-lg text-white text-shadow-sm">Vertical Gradient</span>
                </div>
              </div>
              
              <div className="flex-1 min-w-[250px] h-64 relative rounded-lg overflow-hidden">
                <div className="absolute inset-0 flex">
                  <div className="flex-1 bg-chart-1"></div>
                  <div className="flex-1 bg-chart-2"></div>
                  <div className="flex-1 bg-chart-3"></div>
                  <div className="flex-1 bg-chart-4"></div>
                  <div className="flex-1 bg-chart-5"></div>
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="font-bold text-lg text-white text-shadow-sm">Horizontal Gradient</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-8 p-6 rounded-lg bg-sidebar border-sidebar-border">
            <h2 className="text-xl font-bold mb-4 text-sidebar-foreground">Sidebar Preview</h2>
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 bg-sidebar-accent text-sidebar-accent-foreground p-4 rounded">Accent</div>
              <div className="flex-1 bg-sidebar-primary text-sidebar-primary-foreground p-4 rounded">Primary</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThemeVisualizer;